import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router';

import { Button } from '@/components/orno/button';

import { EmptyState } from './empty-state';
import { PageHeader } from './page-header';

export function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader title={t('notFound.title')} />
      <EmptyState
        icon="search"
        title={t('notFound.title')}
        description={t('notFound.description')}
        action={
          <Link className="btn" to="/">
            {t('actions.goHome')}
          </Link>
        }
      />
    </>
  );
}

function UnexpectedError() {
  const { t } = useTranslation();
  return (
    <div className="narrow-page">
      <EmptyState
        icon="error"
        title={t('errors.unexpectedTitle')}
        description={t('errors.unexpectedDescription')}
        action={
          <div className="cluster">
            <Button variant="acc" onClick={() => window.location.reload()}>
              {t('errors.reload')}
            </Button>
            <a className="btn" href="/">
              {t('actions.goHome')}
            </a>
          </div>
        }
      />
    </div>
  );
}

/** `errorElement` of the router: 404s and anything a route throws while rendering. */
export function RouteErrorPage() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  return <UnexpectedError />;
}

/** The global error boundary around the providers and the router. */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  override state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('Unhandled UI error', error, info.componentStack);
  }

  override render() {
    return this.state.error === null ? this.props.children : <UnexpectedError />;
  }
}

/** Shown while the first lazy route loads. */
export function PageSkeleton() {
  return (
    <div className="page-skeleton stack" aria-hidden="true">
      <orno-skeleton height="32px" width="12rem" />
      <orno-skeleton line width="24rem" />
      <orno-skeleton height="16rem" />
    </div>
  );
}
